import { computed, shallowRef, triggerRef, watch, type ComputedRef } from 'vue'

// useAlbumImageLoading 跟踪当前请求批次的图片加载状态
export const useAlbumImageLoading = <T extends object>(items: ComputedRef<T[]>, requiresImage: (item: T) => boolean) => {
  const pendingImages = shallowRef(new Set<T>())
  const failedImages = shallowRef(new WeakSet<T>())

  watch(items, batch => {
    pendingImages.value = new Set(batch.filter(requiresImage))
  }, { immediate: true, flush: 'sync' })

  // markImageLoaded 仅更新当前批次，忽略旧批次的加载回调
  const markImageLoaded = (item: T) => {
    if (!pendingImages.value.has(item)) return
    const remaining = new Set(pendingImages.value)
    remaining.delete(item)
    pendingImages.value = remaining
  }

  // markImageFailed 结束失败图片的等待，保留失败状态供卡片展示
  const markImageFailed = (item: T) => {
    if (!pendingImages.value.has(item)) return
    failedImages.value.add(item)
    triggerRef(failedImages)
    markImageLoaded(item)
  }

  // isImageLoaded 判断图片是否成功加载
  const isImageLoaded = (item: T) => !pendingImages.value.has(item) && !failedImages.value.has(item)
  // isImageFailed 判断图片是否加载失败
  const isImageFailed = (item: T) => failedImages.value.has(item)
  const imagesSettled = computed(() => pendingImages.value.size === 0)

  return { imagesSettled, markImageLoaded, markImageFailed, isImageLoaded, isImageFailed }
}
